import React from "react"
import {
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Tabs,
  Upload,
} from "antd"
import { MinusCircleOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons"
import { Button } from "src/components/actions/button"
import { CloseSvg, RotateClockwiseSvg, SaveSvg } from "src/components/Svg"
import { TranslatableInput } from "src/components/Translatable"
import { useAppData } from "src/store/app-data"
import { useUploadFile } from "src/hooks/use-upload-file"
import { API_BASE_MEDIA_URL } from "src/config"
import {
  getFormattedLangData,
  getFormData,
  getInitialValues,
  getNestedFormData,
  whiteSpaceValidation,
} from "src/utils/helpers"
import { getLanguages } from "src/utils"
import styled from "styled-components"

const DEFAULT_SECTION = {
  title: { ru: "", en: "" },
  description: { ru: "", en: "" },
  slug: "",
  sortWeight: 0,
  fileId: null,
  fileName: "",
  fileUrl: "",
}

function localizationsToMap(items) {
  if (!Array.isArray(items)) {
    return { ru: "", en: "" }
  }
  return items.reduce(
    (acc, item) => ({ ...acc, [item.language]: item.content }),
    {}
  )
}

export function EditDrawer({ title, initialValues, onClose, onSave }) {
  const layoutConfig = useAppData((state) => state.layoutConfig)
  const [form] = Form.useForm()
  const languages = getLanguages()

  const getDefaultFormValues = React.useCallback(() => {
    if (initialValues) {
      const values = getInitialValues(initialValues)

      const sections =
        Array.isArray(initialValues.sections) && initialValues.sections.length > 0
          ? initialValues.sections.map((section) => ({
              title: localizationsToMap(section.title),
              description: localizationsToMap(section.description),
              slug: section.slug ?? "",
              sortWeight: section.sortWeight ?? 0,
              fileId: section.file?.id ?? null,
              fileName: section.file?.originalName ?? "",
              fileUrl: section.file
                ? `${API_BASE_MEDIA_URL}${section.file.path}/${section.file.systemName}`
                : "",
            }))
          : [DEFAULT_SECTION]

      return { ...values, sections }
    }

    return { sections: [DEFAULT_SECTION] }
  }, [initialValues])

  React.useEffect(() => {
    form.resetFields()
    form.setFieldsValue(getDefaultFormValues())
  }, [form, getDefaultFormValues, initialValues])

  function prepareData(values) {
    return {
      ...(initialValues && { _method: "PUT" }),
      slug: values.slug,
      sortWeight: values.sortWeight ?? 0,
    }
  }

  async function onSubmit(values) {
    const baseData = prepareData(values)
    const formData = getFormData({ values: baseData })

    const langData = getFormattedLangData({ title: values.title })

    const nested = {
      title: langData.title,
    }
    const formDataWithLang = getNestedFormData(nested, formData)

    const sections = values.sections ?? []
    sections.forEach((section, index) => {
      const appendLocalized = (field, value) => {
        const arr = Object.entries(value ?? {}).reduce((acc, [lang, content]) => {
          if (typeof content === "undefined") return acc
          return [...acc, { language: lang, content: content ?? "" }]
        }, [])

        arr.forEach((tr, trIndex) => {
          formDataWithLang.append(
            `sections[${index}][${field}][${trIndex}][language]`,
            tr.language
          )
          formDataWithLang.append(
            `sections[${index}][${field}][${trIndex}][content]`,
            tr.content
          )
        })
      }

      appendLocalized("title", section.title)
      appendLocalized("description", section.description)
      formDataWithLang.append(`sections[${index}][slug]`, section.slug ?? "")
      formDataWithLang.append(
        `sections[${index}][sortWeight]`,
        section.sortWeight ?? 0
      )
      formDataWithLang.append(`sections[${index}][fileId]`, section.fileId ?? "")
    })

    try {
      await onSave(formDataWithLang, initialValues?.id)
      onClose()
      form.resetFields()
    } catch (e) {
      console.warn("save additional info failed", e)
    }
  }

  function handleClose() {
    onClose()
    form.resetFields()
  }

  const handleSectionUpload = async (file, fieldName) => {
    const uploaded = await useUploadFile(file)
    if (uploaded?.id) {
      const url = `${API_BASE_MEDIA_URL}${uploaded.path}/${uploaded.systemName}`
      form.setFields([
        { name: ["sections", fieldName, "fileId"], value: uploaded.id },
        { name: ["sections", fieldName, "fileName"], value: file.name },
        { name: ["sections", fieldName, "fileUrl"], value: url },
      ])
    }
    return false
  }

  const id = initialValues?.id ?? "new"

  return (
    <DrawerEdit
      width={layoutConfig.isMobile ? "90%" : "80%"}
      title={title}
      onClose={handleClose}
      open
    >
      <FormEdit
        form={form}
        layout={"vertical"}
        onFinish={(values) => onSubmit(values)}
      >
        <StyledFormItem label={"Заголовок"}>
          <TranslatableInput name={"title"} id={id} />
        </StyledFormItem>

        <StyledFormItem
          key={id + "_slug"}
          label={"Slug"}
          name={"slug"}
          rules={[
            { required: true, message: "Введите slug!" },
            { validator: whiteSpaceValidation },
          ]}
        >
          <Input placeholder={"about-us"} />
        </StyledFormItem>

        <StyledFormItem
          key={id + "_sortWeight"}
          label={"Позиция в списке"}
          name={"sortWeight"}
          rules={[{ required: true, message: "Введите позицию!" }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} placeholder={"0"} />
        </StyledFormItem>

        <Divider orientation="left">Подразделы</Divider>

        <Form.List
          name="sections"
          initialValue={[DEFAULT_SECTION]}
          rules={[
            {
              validator: async (_, value) => {
                if (!value || value.length < 1) {
                  throw new Error("Должен быть хотя бы один подраздел")
                }
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => (
            <>
              {fields.map(({ key, name, ...field }) => (
                <SectionWrap key={`section_${key}`}>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <StyledFormItem label={"Заголовок подраздела"}>
                        <StyledTabs
                          type="card"
                          size="small"
                          items={languages.map((lang, idx) => ({
                            key: lang.value,
                            label: lang.label,
                            forceRender: true,
                            children: (
                              <Form.Item
                                {...field}
                                name={[name, "title", lang.value]}
                                rules={
                                  idx === 0
                                    ? [
                                        {
                                          required: true,
                                          message: lang.textErrors.required,
                                        },
                                        { validator: whiteSpaceValidation },
                                      ]
                                    : undefined
                                }
                              >
                                <Input />
                              </Form.Item>
                            ),
                          }))}
                        />
                      </StyledFormItem>
                    </Col>

                    <Col span={24}>
                      <StyledFormItem label={"Описание подраздела"}>
                        <StyledTabs
                          type="card"
                          size="small"
                          items={languages.map((lang) => ({
                            key: lang.value,
                            label: lang.label,
                            forceRender: true,
                            children: (
                              <Form.Item
                                {...field}
                                name={[name, "description", lang.value]}
                              >
                                <Input.TextArea autoSize={{ minRows: 2 }} />
                              </Form.Item>
                            ),
                          }))}
                        />
                      </StyledFormItem>
                    </Col>

                    <Col span={12}>
                      <StyledFormItem
                        {...field}
                        label={"Slug подраздела"}
                        name={[name, "slug"]}
                        rules={[
                          { required: true, message: "Введите slug подраздела!" },
                          { validator: whiteSpaceValidation },
                        ]}
                      >
                        <Input placeholder={"contacts"} />
                      </StyledFormItem>
                    </Col>

                    <Col span={4}>
                      <StyledFormItem
                        {...field}
                        label={"Позиция"}
                        name={[name, "sortWeight"]}
                        rules={[{ required: true, message: "Введите позицию!" }]}
                      >
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </StyledFormItem>
                    </Col>

                    <Col span={8}>
                      <StyledFormItem
                        {...field}
                        label={"Файл"}
                        name={[name, "fileId"]}
                        rules={[{ required: true, message: "Загрузите файл!" }]}
                      >
                        <Input type="hidden" />
                      </StyledFormItem>

                      <Form.Item shouldUpdate noStyle>
                        {() => {
                          const fileName = form.getFieldValue([
                            "sections",
                            name,
                            "fileName",
                          ])
                          const fileUrl = form.getFieldValue([
                            "sections",
                            name,
                            "fileUrl",
                          ])
                          return (
                            <Upload
                              beforeUpload={(file) =>
                                handleSectionUpload(file, name)
                              }
                              showUploadList={false}
                              maxCount={1}
                            >
                              <Button icon={<UploadOutlined />}>
                                {fileName
                                  ? "Заменить файл"
                                  : "Загрузить файл"}
                              </Button>
                              {fileName ? (
                                <FileMeta>
                                  <FileName
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {fileName}
                                  </FileName>
                                </FileMeta>
                              ) : null}
                            </Upload>
                          )
                        }}
                      </Form.Item>

                      <StyledFormItem
                        {...field}
                        name={[name, "fileName"]}
                        noStyle
                      >
                        <Input type="hidden" />
                      </StyledFormItem>

                      <StyledFormItem
                        {...field}
                        name={[name, "fileUrl"]}
                        noStyle
                      >
                        <Input type="hidden" />
                      </StyledFormItem>
                    </Col>
                  </Row>

                  {fields.length > 1 && (
                    <RemoveRow>
                      <Button
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                      >
                        Удалить подраздел
                      </Button>
                    </RemoveRow>
                  )}
                </SectionWrap>
              ))}

              <Form.ErrorList errors={errors} />

              <StyledFormItem>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add(DEFAULT_SECTION)}
                  style={{ width: "100%" }}
                >
                  Добавить подраздел
                </Button>
              </StyledFormItem>
            </>
          )}
        </Form.List>

        <ButtonRow>
          <ButtonCol>
            <Button
              danger
              onClick={() => form.setFieldsValue(getDefaultFormValues())}
              icon={<RotateClockwiseSvg />}
            >
              Вернуть
            </Button>
          </ButtonCol>
          <ButtonCol>
            <Button onClick={handleClose} icon={<CloseSvg />}>
              Закрыть
            </Button>
          </ButtonCol>
          <ButtonCol>
            <Button icon={<SaveSvg />} type="primary" htmlType="submit">
              Сохранить
            </Button>
          </ButtonCol>
        </ButtonRow>
      </FormEdit>
    </DrawerEdit>
  )
}

const DrawerEdit = styled(Drawer)``

const FormEdit = styled(Form)``

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`

const SectionWrap = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
`

const RemoveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`

const FileMeta = styled.div`
  margin-top: 8px;
`

const FileName = styled.a`
  font-size: 13px;
  word-break: break-all;
`

const ButtonCol = styled(Col)`
  padding-bottom: 5px;
`

const ButtonRow = styled(Row)`
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`

const StyledTabs = styled(Tabs)`
  overflow: visible;

  &.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
    border-bottom: none;
    user-select: none;
  }

  &.ant-tabs-card.ant-tabs-small > .ant-tabs-nav .ant-tabs-tab {
    padding: 3px 7px 6px;
  }

  &.ant-tabs-top > .ant-tabs-nav {
    bottom: -3px;
    margin-bottom: 0;

    &::before {
      border-bottom: none;
    }
  }

  .ant-form-item {
    margin-bottom: 0;
  }
`
